import { Constructor, present } from './typescript';

export interface EventHandler<Event> {
  (event: Event): any | Promise<any>;
}

export interface OffCurried {
  (): void;
}

export abstract class BaseEvent {
  handled = false;
}

class HandlerWrapper {
  constructor(
    public handler: EventHandler<any>,
    public priority: number,
  ) {
  }
}

export default class EventBus {
  // eslint-disable-next-line @typescript-eslint/ban-types
  private map = new Map<Function, Array<HandlerWrapper>>();

  on<Event extends BaseEvent>(type: Constructor<Event>, handler: EventHandler<Event>, priority = 1000): OffCurried {
    const wrapper = new HandlerWrapper(handler, priority);
    if (this.map.has(type)) {
      const wrappers = present(this.map.get(type));
      wrappers.push(wrapper);
      wrappers.sort((a, b) => a.priority - b.priority);
    } else {
      this.map.set(type, [wrapper]);
    }
    return () => this.off(type, handler);
  }

  off<Event extends BaseEvent>(type: Constructor<Event>, handler: EventHandler<Event>): void {
    const wrappers = this.map.get(type);
    if (wrappers) {
      const i = wrappers.findIndex(w => w.handler === handler);
      if (i >= 0) {
        wrappers.splice(i, 1);
        if (wrappers.length === 0) this.map.delete(type);
      }
    }
  }

  async fire(event: BaseEvent): Promise<void> {
    const wrappers = this.map.get(event.constructor);
    if (!wrappers) return;
    for (const wrapper of wrappers) {
      await wrapper.handler(event); // eslint-disable-line no-await-in-loop
      if (event.handled) break;
    }
  }
}